/**
 * The price engine — the user's own scheme (2026-08-18, most of Q7):
 *
 *   dagverhuur      € 100 per dag
 *   weekendverhuur  minimum € 200, of € 4 per persoon per nacht
 *   kampverhuur     minimum € 1000, of € 4 per persoon per nacht
 *
 * One flat tariff for every group type — the jeugd/volwassenen split is gone on
 * the user's instruction. ⚠ §7 (werkingssubsidie) records "youth substantially
 * below adult" as a legal condition; the backlog carries the flag to verify the
 * flat tariff against it before any subsidy declaration is filed.
 *
 * The verhuurtype is DERIVED from the dates (see verhuurtype.ts): the seasons
 * rule is part of pricing. A single day is dagverhuur the whole year round
 * (2026-08-21); overnight summer stays price as kamp.
 *
 * Lines carry a machine-readable `kind`, never text: callers compose the Dutch
 * that lands in `booking_line`. Note: 0001_init.sql's `price_rule` has no
 * verhuurtype column yet — ticket 0.8 owes it a small migration; until then the
 * seed maps verhuurtype rules onto the existing columns for storage.
 *
 * Prices freeze at `confirmed` (see `canEditPrices`); adjustment afterwards
 * only via the append-only `price_adjustment` with a mandatory reason.
 */

import { addCents, type Cents, cents, multiplyCents, subtractCents } from "./money.js";
import { compareDates, nightsBetween, type PlainDate } from "./time.js";
import { type Verhuurtype, verhuurtypeVoor } from "./verhuurtype.js";

/** The building sleeps 60; this is a sanity bound, not a capacity rule. */
export const MAX_PERSONS = 10_000;

/**
 * The standard scheme (the user's numbers, 2026-08-18) — the SEED's birth
 * values; since 0.25 the quote, the cards and the confirm transition all read
 * the price_rule rows this seeds. The waarborg amounts are PLACEHOLDERS
 * pending Q7 (board material, deliberately off the public site); the deposit
 * lifecycle itself is flagged for redesign on Q13 (voorschot).
 */
export const STANDAARD_TARIEVEN: Record<
  Verhuurtype,
  {
    readonly perPersonPerNightCents: Cents | null;
    readonly perDayCents: Cents | null;
    readonly minimumCents: Cents;
    readonly waarborgCents: Cents;
  }
> = {
  dag: {
    perPersonPerNightCents: null,
    perDayCents: cents(10_000),
    minimumCents: cents(0),
    waarborgCents: cents(15_000),
  },
  weekend: {
    perPersonPerNightCents: cents(400),
    perDayCents: null,
    minimumCents: cents(20_000),
    waarborgCents: cents(30_000),
  },
  kamp: {
    perPersonPerNightCents: cents(400),
    perDayCents: null,
    minimumCents: cents(100_000),
    waarborgCents: cents(30_000),
  },
};

/** The standard scheme as engine rules for one space. */
export function standaardRegels(spaceId: string): PriceRule[] {
  return (Object.keys(STANDAARD_TARIEVEN) as Verhuurtype[]).map((verhuurtype) => ({
    spaceId,
    verhuurtype,
    validFrom: null,
    validTo: null,
    ...STANDAARD_TARIEVEN[verhuurtype],
  }));
}

/** One tariff for one renting type. NULL bounds mean "always valid". */
export type PriceRule = {
  readonly spaceId: string;
  readonly verhuurtype: Verhuurtype;
  readonly validFrom: PlainDate | null;
  readonly validTo: PlainDate | null;
  readonly perPersonPerNightCents: Cents | null;
  readonly perDayCents: Cents | null;
  readonly minimumCents: Cents;
  readonly waarborgCents: Cents;
};

export type QuoteRequest = {
  readonly spaceId: string;
  readonly arrival: PlainDate;
  readonly departure: PlainDate;
  readonly persons: number;
};

/**
 * What a line is, not what it says. `minimum_top_up` is the shortfall to the
 * minimum — its own line, so the renter sees why the total is not
 * nights × persons.
 */
export type PriceLineKind = "overnight_persons" | "day" | "minimum_top_up";

export type PriceLine = {
  readonly kind: PriceLineKind;
  /** Whole units — nights × persons, or 1. Never fractional. */
  readonly quantity: number;
  readonly unitCents: Cents;
  readonly amountCents: Cents;
};

export type Quote = {
  readonly verhuurtype: Verhuurtype;
  readonly lines: readonly PriceLine[];
  readonly totalCents: Cents;
};

/**
 * Failure is machine-readable; the Dutch a user sees is looked up by the
 * caller. `ambiguous_rules` is deliberate: two rules matching the same stay is
 * a data problem, and quietly picking one would price bookings differently
 * depending on row order.
 */
export type QuoteResult =
  | { readonly ok: true; readonly quote: Quote }
  | {
      readonly ok: false;
      readonly reason:
        | "invalid_stay"
        | "geen_kamp_in_schooljaar"
        | "seizoensgrens"
        | "no_rule"
        | "ambiguous_rules"
        | "no_rate";
    };

/**
 * Valid on the arrival date. NULL bounds are open ends.
 *
 * Both bounds are INCLUSIVE — unlike every other range in this repo, which is
 * half-open `[from, to)`. A tariff end date is written by a human as "geldig
 * t/m", and `CHECK (valid_to >= valid_from)` permits a one-day window. Do not
 * "fix" this to `[)` when building period resolution: that shifts every tariff
 * boundary by a day.
 */
function appliesOn(rule: PriceRule, arrival: PlainDate): boolean {
  if (rule.validFrom !== null && compareDates(arrival, rule.validFrom) < 0) return false;
  if (rule.validTo !== null && compareDates(arrival, rule.validTo) > 0) return false;
  return true;
}

/** The tariff shown on a card: the rule's amounts without its plumbing.
 *  waarborgCents is included for the BEHEER screen — the public endpoint
 *  strips it (contract material, deliberately off the public site — Q7). */
export type Tarief = {
  readonly verhuurtype: Verhuurtype;
  readonly perPersonPerNightCents: Cents | null;
  readonly perDayCents: Cents | null;
  readonly minimumCents: Cents;
  readonly waarborgCents: Cents;
};

/**
 * The tariff in force per verhuurtype on one date — what the public cards and
 * the beheer screen display (0.25). Same matching as priceQuote, so the card
 * can never contradict the quote; a verhuurtype with zero or multiple matching
 * rules is OMITTED rather than guessed (the quote would refuse it too).
 */
export function geldendeTarieven(rules: readonly PriceRule[], on: PlainDate): Tarief[] {
  const result: Tarief[] = [];
  for (const verhuurtype of ["dag", "weekend", "kamp"] as const) {
    const matching = rules.filter(
      (rule) => rule.verhuurtype === verhuurtype && appliesOn(rule, on),
    );
    const rule = matching.length === 1 ? matching[0] : undefined;
    if (rule) {
      result.push({
        verhuurtype,
        perPersonPerNightCents: rule.perPersonPerNightCents,
        perDayCents: rule.perDayCents,
        minimumCents: rule.minimumCents,
        waarborgCents: rule.waarborgCents,
      });
    }
  }
  return result;
}

/**
 * Quote a stay, or explain why it cannot be priced.
 *
 * Worked example — weekend, 38 personen:
 *   2 nachten × 38 personen × € 4,00 = € 304,00 (boven het minimum van € 200)
 *
 * Small-group example — weekend, 20 personen, 2 nachten:
 *   2 × 20 × € 4,00 = € 160,00 < minimum € 200,00 → bijpassing € 40,00.
 *   Een kleine groep in een groot gebouw kost nog steeds het gebouw.
 *
 * Camp example — 44 personen, 10 nachten:
 *   10 × 44 × € 4,00 = € 1.760,00 (boven het minimum van € 1.000)
 */
export function priceQuote(rules: readonly PriceRule[], request: QuoteRequest): QuoteResult {
  // Swapped dates are the most common mistake on a renter-facing form — an
  // expected failure, not a programmer error, so check before nightsBetween
  // (which throws on a reversed pair).
  if (compareDates(request.departure, request.arrival) < 0) {
    return { ok: false, reason: "invalid_stay" };
  }
  if (
    !(
      request.persons > 0 &&
      Number.isInteger(request.persons) &&
      // Far above any real group, far below where cents() could overflow.
      request.persons <= MAX_PERSONS
    )
  ) {
    return { ok: false, reason: "invalid_stay" };
  }

  const typed = verhuurtypeVoor(request.arrival, request.departure);
  if (!typed.ok) {
    return { ok: false, reason: typed.reason === "ongeldig" ? "invalid_stay" : typed.reason };
  }
  const nights = nightsBetween(request.arrival, request.departure);

  const matching = rules.filter(
    (rule) =>
      rule.spaceId === request.spaceId &&
      rule.verhuurtype === typed.verhuurtype &&
      appliesOn(rule, request.arrival),
  );
  if (matching.length === 0) return { ok: false, reason: "no_rule" };
  if (matching.length > 1) return { ok: false, reason: "ambiguous_rules" };
  const rule = matching[0];
  if (!rule) return { ok: false, reason: "no_rule" };

  const base = baseLine(rule, typed.verhuurtype, nights, request.persons);
  if (base === undefined) return { ok: false, reason: "no_rate" };

  const lines: PriceLine[] = [];
  let subtotal: Cents = cents(0);

  // Zero-amount lines are omitted: an all-zero tariff quotes as empty.
  if (base.amountCents > 0) {
    lines.push(base);
    subtotal = addCents(subtotal, base.amountCents);
  }
  if (subtotal < rule.minimumCents) {
    lines.push(line("minimum_top_up", 1, subtractCents(rule.minimumCents, subtotal)));
    subtotal = rule.minimumCents;
  }

  return { ok: true, quote: { verhuurtype: typed.verhuurtype, lines, totalCents: subtotal } };
}

/**
 * The Dutch that lands in `booking_line` rows — document data, like the audit
 * labels in booking-state.ts, so it lives here once instead of drifting apart
 * between the seed and the API. UI display labels stay in nl.ts.
 */
export function omschrijfPrijslijn(
  kind: PriceLineKind,
  verhuurtype: Verhuurtype,
  nachten: number,
  personen: number,
): string {
  const naam: Record<Verhuurtype, string> = {
    dag: "Dagverhuur",
    weekend: "Weekendverhuur",
    kamp: "Kampverhuur",
  };
  switch (kind) {
    case "day":
      return "Dagverhuur";
    case "minimum_top_up":
      return "Bijpassing minimumtarief";
    case "overnight_persons":
      return `${naam[verhuurtype]} — ${nachten} ${nachten === 1 ? "nacht" : "nachten"} × ${personen} personen`;
  }
}

function baseLine(
  rule: PriceRule,
  verhuurtype: Verhuurtype,
  nights: number,
  persons: number,
): PriceLine | undefined {
  if (verhuurtype === "dag") {
    // One whole calendar day — no per-hour arithmetic to get wrong.
    return rule.perDayCents === null ? undefined : line("day", 1, rule.perDayCents);
  }
  if (rule.perPersonPerNightCents === null) return undefined;
  return line("overnight_persons", nights * persons, rule.perPersonPerNightCents);
}

function line(kind: PriceLineKind, quantity: number, unitCents: Cents): PriceLine {
  return { kind, quantity, unitCents, amountCents: multiplyCents(unitCents, quantity) };
}
